import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

// Google Analytics n'est chargé qu'après un consentement explicite (exigence CNIL).
export const GA_ID = 'G-9MML5G8BSF';
const STORAGE_KEY = 'cg_cookie_consent';
// La CNIL recommande de redemander le choix au bout de 6 mois.
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182;
export const OPEN_CONSENT_EVENT = 'cg:open-cookie-consent';

function readChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { value, date } = JSON.parse(raw);
    if (!date || Date.now() - date > MAX_AGE_MS) return null;
    return value === 'accepted' || value === 'refused' ? value : null;
  } catch {
    return null;
  }
}

function saveChoice(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, date: Date.now() }));
  } catch {
    /* stockage indisponible : le bandeau réapparaîtra à la prochaine visite */
  }
}

function loadAnalytics() {
  if (window.__cgAnalyticsLoaded) return;
  window.__cgAnalyticsLoaded = true;
  window[`ga-disable-${GA_ID}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // cookie_expires : 13 mois maximum (recommandation CNIL), au lieu de 2 ans par défaut.
  window.gtag('config', GA_ID, { cookie_expires: 60 * 60 * 24 * 395 });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function removeAnalyticsCookies() {
  window[`ga-disable-${GA_ID}`] = true;
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_') || name === '_gid') {
      domains.forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
      });
    }
  });
}

export function openCookieConsent() {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = readChoice();
    if (choice === 'accepted') loadAnalytics();
    else setVisible(choice === null);

    const reopen = () => setVisible(true);
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  const accept = () => {
    saveChoice('accepted');
    loadAnalytics();
    setVisible(false);
  };

  const refuse = () => {
    const wasLoaded = window.__cgAnalyticsLoaded;
    saveChoice('refused');
    removeAnalyticsCookies();
    setVisible(false);
    // Le script Google déjà chargé ne peut pas être retiré : on recharge la page.
    if (wasLoaded) window.location.reload();
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Gestion des cookies"
      className="fixed bottom-0 inset-x-0 z-[100] p-4"
    >
      <div className="max-w-3xl mx-auto bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Cookie className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-zinc-300 text-sm leading-relaxed">
            Avec votre accord, nous utilisons Google Analytics pour mesurer l'audience du site.
            Ces cookies ne sont déposés que si vous acceptez, et vous pouvez changer d'avis à tout moment.{' '}
            <Link to="/confidentialite" className="text-blue-400 underline hover:text-blue-300">En savoir plus</Link>
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={refuse}
            className="flex-1 md:flex-none px-5 py-2.5 border border-zinc-600 text-white hover:bg-zinc-800 font-mono text-xs uppercase font-bold rounded-none transition-colors"
          >
            Refuser
          </button>
          <button
            onClick={accept}
            className="flex-1 md:flex-none px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs uppercase font-bold rounded-none transition-colors"
          >
            Accepter
          </button>
        </div>
      </div>
    </div>
  );
}
